/**
 * TileLoadingIndicator.tsx — subtle, non-blocking "tiles are loading" pill (T-229)
 *
 * A blank map while tiles fetch reads as "nothing here" or "broken". This shows a small
 * spinner pill WHILE map tile/source data for the current view is loading and hides once
 * rendering settles — so the user knows data is on its way rather than absent.
 *
 * Driven purely by MapLibre lifecycle events (no coupling to any specific tier/transport):
 *   - 'dataloading' — a source began loading data for the current view
 *   - 'idle'        — all tiles for the view are loaded and no transitions remain (render done)
 *
 * Two timers keep it from being noisy:
 *   - debounceMs: only appear if loading is still in flight after this delay. An instant load
 *     (cached tiles) or a tier that legitimately has NO data for the view — which settles to
 *     'idle' almost immediately — never trips the indicator (loading != empty). We also re-check
 *     map.areTilesLoaded() at the debounce boundary as a belt-and-braces guard.
 *   - minDisplayMs: once shown, stay up at least this long so a load that finishes right after
 *     the pill appears doesn't produce a one-frame flicker.
 *
 * pointer-events-none: the pill is purely informational and never intercepts map interaction.
 * bg-card/text-foreground are theme tokens, so it reads correctly in light and dark.
 */
import { useEffect, useRef, useState } from 'react'
import { useMap } from './MapContext'
import { Spinner } from '../ui/spinner'

export interface TileLoadingIndicatorProps {
  /** Only show if loading is still in flight after this many ms (suppresses instant-load flashes). */
  debounceMs?: number
  /** Once shown, keep the pill visible at least this many ms (suppresses flicker). */
  minDisplayMs?: number
}

export function TileLoadingIndicator({
  debounceMs = 200,
  minDisplayMs = 400,
}: TileLoadingIndicatorProps = {}) {
  const map = useMap()
  const [visible, setVisible] = useState(false)
  // Mirror `visible` in a ref so the event handlers read the latest value without the effect
  // re-subscribing (and thus dropping/re-adding map listeners) on every show/hide toggle.
  const visibleRef = useRef(false)
  visibleRef.current = visible
  const shownAtRef = useRef(0)
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!map) return

    const clearShow = () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current)
        showTimerRef.current = null
      }
    }
    const clearHide = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current)
        hideTimerRef.current = null
      }
    }

    const show = () => {
      setVisible(true)
      shownAtRef.current = Date.now()
    }

    const onLoading = () => {
      // A load started (or continued): cancel any pending hide. Schedule the appearance once,
      // after the debounce, and only if tiles are genuinely still loading by then.
      clearHide()
      if (visibleRef.current || showTimerRef.current) return
      showTimerRef.current = setTimeout(() => {
        showTimerRef.current = null
        const tilesLoaded = typeof map.areTilesLoaded === 'function' ? map.areTilesLoaded() : false
        if (!tilesLoaded) show()
      }, debounceMs)
    }

    const onIdle = () => {
      // Everything settled. Cancel a pending (not-yet-shown) appearance so a fast load never
      // flashes. If the pill is up, hide it after honouring the minimum display window.
      clearShow()
      if (!visibleRef.current) return
      const elapsed = Date.now() - shownAtRef.current
      const wait = Math.max(0, minDisplayMs - elapsed)
      clearHide()
      hideTimerRef.current = setTimeout(() => {
        hideTimerRef.current = null
        setVisible(false)
      }, wait)
    }

    map.on('dataloading', onLoading)
    map.on('idle', onIdle)

    return () => {
      clearShow()
      clearHide()
      map.off('dataloading', onLoading)
      map.off('idle', onIdle)
    }
  }, [map, debounceMs, minDisplayMs])

  if (!visible) return null

  return (
    <div className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 z-20">
      <div className="flex items-center gap-2 rounded-full bg-card/95 px-3 py-1.5 shadow-md text-xs font-medium text-foreground">
        <Spinner size={14} label="Loading map data" />
        Loading map…
      </div>
    </div>
  )
}
